import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-enterprise";
import api from "../../utils/axios";
import { useNavigate } from "react-router-dom";
import {
	Button,
	Divider,
	Form,
	Modal,
	Select,
	Spin,
	Switch,
	message,
} from "antd";
import DefaultInput from "../../components/form/DefaultInput";
import DefaultSelect from "../../components/form/DefaultSelect";
import classes from "./index.module.css";
import { ReactComponent as RegisterIcon } from "../../assets/add-plus.svg";
import { ReactComponent as ExportIcon } from "../../assets/arrow-download-small.svg";
import { ReactComponent as SearchIcon } from "../../assets/search-find.svg";
import OrderStatus from "../../components/OrderStatus";
import PaginationTable from "../../components/Pagination";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { fetchZones, getConfigurationValue } from "../../lib/OptionListApi";
import { string } from "yargs";
import { priceFormatter, thousandsFormatter } from "../../utils/formatters";
import DefaultPhoneInput from "../../components/form/DefaultPhoneInput";
import FinanceStatistic from "../../components/FinanceStatistic";
import { exportDataToCsv } from "../../utils/exportDataToCsv";
import { usernamePasswordValidate } from "../../types/UsernamePasswordValidate";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import DefaultPasswordInput from "../../components/form/DefaultPasswordInput";
import useLang from "../../hooks/useLang";
import { useMediaQuery } from "@mui/material";
import { hasAccess } from "../../lib/MenuValidation";

interface RowDataExtended {
	id: number;
	firstName: string;
	phone: string;
	username: string;
	email: string;
	numberOfOrders?: number;
	delivered: string;
	currentBalance: number;
	dailySales: number;
}

export interface Driver {
	id: number;
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	numberOfOrders?: number;
	delivered?: number;
	currentBalance?: number;
	driverSalesOwedToCompany?: number;
	dailySales?: number;
	cityId: number;
	countryId: number;
	country?: string;
	city?: string;
	createdAt?: string;
	tarifaKs: number;
	tarifaAl: number;
	tarifaMk: number;
}

const { Option } = Select;

const Driver = () => {
	const [open, setOpen] = useState<boolean>(false);
	const navigate = useNavigate();

	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	let grid = useRef(null);
	const [tableData, setTableData] = useState<Driver[]>();
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [fullName, setFullName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [address, setAddress] = useState("");
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [zones, setZones] = useState<any>([]);
	const [countryDropDown, setCountryDropDown] = useState<any>([]);
	const [cityDropDown, setCityDropDown] = useState<any>([]);
	const [cities, setCities] = useState<any>([]);
	const [sectionDropDown, setSectionDropDown] = useState<any>([]);
	const [sections, setSections] = useState<any>([]);
	const [country, setCountry] = useState<string | undefined>();
	const [city, setCity] = useState<string | undefined>();
	const [sectionId, setSectionId] = useState<string | undefined>();
	const [identificationNumber, setIdentificationNumber] = useState<
		string | undefined
	>();
	const [personalIdLength, setPersonalIdLength] = useState<number>();
	const [totalTableCount, setTotalTableCount] = useState<number>(0);
	const [codeId, setCodeId] = useState<string | undefined>();
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [loading, setLoading] = useState<boolean>(false);
	const { strings } = useLang("driver");

	const hasAccessToDriverDetails = hasAccess("DRIVER_DETAILS_ACCESS");

	const nameAndSurnameGetter = (params: any) => {
		const text = params.data.firstName + " " + params.data.lastName;
		return text;
	};

	const getIdentificationNumberAllowedLength = async () => {
		try {
			setPersonalIdLength(parseInt(await getConfigurationValue(3)));
		} catch (error: any) {
			console.log(error);
		}
	};

	const getZones = useCallback(async () => {
		const allZones = await fetchZones();
		if (allZones) {
			const countries = allZones
				.filter((item: any) => item.levelId === 1)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCountryDropDown(countries);
			const filteredCities = allZones
				.filter((item: any) => item.levelId === 2)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCityDropDown(filteredCities);
			const cities = allZones?.filter((item: any) => item.levelId === 2);
			setCities(cities);
			const filteredSections = allZones
				.filter((item: any) => item.levelId === 3)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setSectionDropDown(filteredSections);
			const sections = allZones.filter((item: any) => item.levelId === 3);
			setSections(sections);
		}
		setZones(allZones);
	}, []);

	const fetchDrivers = useCallback(
		async (
			countryId: any,
			cityId: any,
			searchTerm: any,
			pageSize: any,
			currentPage: any
		) => {
			setLoading(true);
			try {
				const [dataRes, countRes] = await Promise.all([
					api.get<Driver[]>("User/drivers-web", {
						params: {
							countryId: countryId,
							cityId: cityId,
							searchTerm: searchTerm,
							pageSize: pageSize,
							currentPage: currentPage,
						},
					}),
					api.get<number>("User/drivers-count-for-web", {
						params: {
							countryId: countryId,
							cityId: cityId,
							searchTerm: searchTerm,
						},
					}),
				]);
				setTableData(dataRes.data);
				setTotalTableCount(countRes.data);
			} catch (err) {
				console.error("fetchDrivers error", err);
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	useEffect(() => {
		getZones();
		getIdentificationNumberAllowedLength();
		fetchDrivers(null, null, null, pageSize, currentPage);
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 50,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "id",
				headerName: strings?.code,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
				}),
				width: 70,
				suppressSizeToFit: isSmallScreen,
			},

			{
				field: "firstName",
				valueGetter: nameAndSurnameGetter,
				headerName: strings?.nameSurname,
				cellStyle: (params: any) => ({
					fontSize: "14px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					fontFamily: "Inter",
					textDecoration: "underline",
				}),
				// width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "phone",
				headerName: strings?.phone,
				// width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			// {
			// 	field: "email",
			// 	headerName: "Email",
			// 	// width: 130,
			// },
			{
				field: "numberOfOrders",
				headerName: strings?.numberOfOrders,
				// width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "delivered",
				headerName: strings?.delivered,
				// width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "currentBalance",
				headerName: strings?.currentBalance,
				// width: 150,
				valueFormatter: (params) => thousandsFormatter(params.value),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "driverSalesOwedToCompany",
				headerName: strings?.driverSalesOwedToCompany,
				// width: 130,
				valueFormatter: (params) => thousandsFormatter(params.value),
			},
			{
				field: "dailySales",
				headerName: strings?.dailySales,
				// width: 130,
				valueFormatter: (params) => thousandsFormatter(params.value),
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [fetchDrivers, getZones, isSmallScreen, setTableColumns, strings]);

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "id":
				await onDriverIdClicked(params?.data);
				break;
			case "firstName":
				await onDriverIdClicked(params?.data);
				break;
		}
	};
	const onDriverIdClicked = (data: any) => {
		const driverId = data?.id?.toString();
		// navigate("/driver-details", { state: driverId });
		if (hasAccessToDriverDetails) {
			const url = `/driver-details?id=${driverId}`;
			window.open(url, "_blank");
		} else {
			toast.error("Error! Nuk keni qasje ne detaje te shofereve");
			return;
		}
	};

	const validateInsertedData = (data: any) => {
		const {
			firstName,
			lastName,
			phone,
			address,
			username,
			password,
			sectionId,
			identificationNumber,
		} = data;
		if (!firstName || firstName.length === 0) {
			toast.error("Emri është i detyrueshëm");
			return false;
		}
		if (!lastName || lastName.length === 0) {
			toast.error("Mbiemri është i detyrueshëm");
			return false;
		}
		if (!address || address.length === 0) {
			toast.error("Adresa është e detyrueshme");
			return false;
		}
		if (!phone || phone.length === 0) {
			toast.error("Numri i telefonit është i detyrueshëm");
			return false;
		}
		if (!identificationNumber || identificationNumber.length === 0) {
			toast.error("Nr.Leternjoftimit është i detyrueshëm.");
			return false;
		}
		if (!sectionId) {
			toast.error("Zona është e detyrueshme.");
			return false;
		}
		if (identificationNumber?.length !== personalIdLength) {
			toast.error(
				"Numri i leternjoftimit duhet të jetë i gjatë 10 karaktere."
			);
			return false;
		}
		return true;
	};

	const InsertDriver = async () => {
		const fullNameArray = fullName.split(" ");
		const firstName = fullNameArray[0] || "";
		const lastName = fullNameArray.slice(1).join(" ");

		const validation = usernamePasswordValidate(username, password);

		if (!validation.valid) {
			toast.error(validation.message);
			return;
		}

		const driver = {
			firstName: firstName,
			lastName: lastName,
			email: email,
			username: username,
			password: password,
			phone: phone,
			address: address,
			sectionId: sectionId,
			identificationNumber: identificationNumber,
		};
		if (validateInsertedData(driver)) {
			try {
				const response = await api.post("User/insertdriver", driver);

				if (response.status === 200) {
					// await getTableData();
					await fetchDrivers(null, null, null, pageSize, currentPage);
					toast.success("Shoferi u shtua me sukses!");
					closeModalAndCleanState();
					return;
				} else {
					toast.error(
						"Një gabim ndodhi gjatë krijimit. Ju lutemi provoni përsëri më vonë"
					);
				}
			} catch (error: any) {
				console.error("Error creating account:", error.response.data);
			}
		} else {
			return;
		}
	};

	const startIndex = (currentPage - 1) * pageSize + 1;
	const endIndex =
		startIndex + Math.min(pageSize, totalTableCount - startIndex);
	const onDriverPhoneNumberChange = (value: any) => {
		setPhone(value);
	};

	const onPageChanged = (page: number, pageSize: number) => {
		setCurrentPage(page);
		if (pageSize && pageSize !== pageSize) {
			setPageSize(pageSize);
			setCurrentPage(1);
		}
	};
	const handlePagination = (page: number, size?: number) => {
		// size is undefined when only page changes
		if (size && size !== pageSize) {
			setPageSize(size);
			setCurrentPage(1);
		} else {
			setCurrentPage(page);
		}
	};

	const handleSearchChange = (e: any) => {
		setCodeId(e.target.value);
		setCurrentPage(1);
	};
	const onCountrySelect = (e: any) => {
		setCity("");
		const filteredCities = cities
			.filter((item: any) => item.parentId === e)
			.map((item: any) => ({
				value: item.id,
				label: item.description,
			}));
		setCityDropDown(filteredCities);
		setCountry(e);
		setCurrentPage(1);
	};
	const onCitySelect = (e: any) => {
		setSectionId("");
		const filteredSections = sections
			.filter((item: any) => item.parentId === e)
			.map((item: any) => ({
				value: item.id,
				label: item.description,
			}));
		setSectionDropDown(filteredSections);
		setCity(e);
		setCurrentPage(1);
	};

	const onBtExport = useCallback(() => {
		if (grid) {
			(grid.current as any).api.exportDataAsExcel();
		}
	}, []);

	const selectedColumnIds = [
		"id",
		"firstName",
		"phone",
		"username",
		"email",
		"numberOfOrders",
		"delivered",
		"currentBalance",
		"dailySales",
	];

	const onExportSelectedId = async () => {
		try {
			const response = await api.get<Driver[]>("User/drivers-web", {
				params: {
					countryId: country,
					cityId: city,
					searchTerm: codeId,
					pageSize: totalTableCount,
					currentPage,
				},
			});
			if (response.status === 200 && response.data.length > 0) {
				const dataToExport = response?.data.map((item: any) => ({
					...item,
					firstName: `${item.firstName} ${item.lastName}`,
					dailySales: thousandsFormatter(item.dailySales),
					currentBalance: thousandsFormatter(item.currentBalance),
					driverSalesOwedToCompany: thousandsFormatter(
						item.driverSalesOwedToCompany
					),
				}));
				exportDataToCsv(grid, selectedColumnIds, dataToExport);
			}
		} catch (error) {
			console.error("Error exporting Excel file:", error);
			toast.error("Failed to export to Excel.");
		}
	};

	const onFirstDataRendered = useCallback((params: any) => {
		(grid.current as any).api.sizeColumnsToFit();
	}, []);

	const [error, setError] = useState<boolean>(false);

	const handleConfirmPasswordChange = (e: any) => {
		setConfirmPassword(e.target.value);
		if (password === e.target.value) {
			setError(false);
		}
	};

	const handleBlur = useCallback(() => {
		if (password !== confirmPassword) {
			message.error("Fjalëkalimet nuk përputhen.");
			setError(true);
		} else {
			setError(false);
		}
	}, [confirmPassword, password]);

	const closeModalAndCleanState = () => {
		setFullName("");
		setEmail("");
		setUsername("");
		setPassword("");
		setConfirmPassword("");
		setPhone("");
		setAddress("");
		setCountry("");
		setCity("");
		setIdentificationNumber("");
		setSectionId("");
		setOpen(false);
	};

	const handleFilterSubmit = async () => {
		await fetchDrivers(country, city, codeId, pageSize, currentPage);
	};

	return (
		<>
			<div className={classes.mainContainer}>
				{/* <FinanceStatistic /> */}

				<div className={classes.heroContainer}>
					<div className={classes.firstContainer}>
						<h1 className={classes.title}>
							{strings?.title}
							<span className={classes.clientsCount}>
								({tableData?.length})
							</span>
						</h1>
						<div className={classes.buttons}>
							<Button
								className={classes.button}
								onClick={onExportSelectedId}
							>
								<ExportIcon />
								{strings?.exportButton}
							</Button>
							<Button
								className={classes.button}
								onClick={() => setOpen(true)}
							>
								<RegisterIcon />
								{strings?.registerButton}
							</Button>
						</div>
					</div>
					<div className={classes.inputs}>
						<DefaultInput
							label={strings?.searchInput.label}
							className={classes.input}
							placeholder={strings?.searchInput.placeholder}
							prefix={<SearchIcon />}
							value={codeId}
							onChange={handleSearchChange}
							allowClear
						/>
						<DefaultSelect
							label={strings?.countrySelect.label}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							placeholder={strings?.countrySelect.placeholder}
							options={countryDropDown}
							value={country}
							onChange={onCountrySelect}
							allowClear
						/>
						<DefaultSelect
							label={strings?.citySelect.label}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							placeholder={strings?.citySelect.placeholder}
							options={cityDropDown}
							value={city}
							onChange={onCitySelect}
							allowClear
						/>
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								paddingTop: 15,
							}}
						>
							<Button
								onClick={handleFilterSubmit}
								// disabled={filterButtonLoading}
								style={{
									width: "9.25rem",
									height: "42px",
									background: "#FFFFFF",
									borderRadius: "4px",
									color: "white",
									backgroundColor: "#4f16c2",
									alignSelf: "center",
								}}
							>
								{"Filtro"}
							</Button>
						</div>
					</div>
					{loading ? (
						<div style={{ padding: "40px", textAlign: "center" }}>
							<Spin size="large" />
						</div>
					) : (
						<>
							<div className={classes.pagination}>
								<PaginationTable
									current={currentPage}
									pageSize={pageSize}
									total={totalTableCount}
									startOrderIndex={startIndex}
									endOrderIndex={endIndex}
									onChange={handlePagination}
									onShowSizeChange={handlePagination}
								/>
							</div>

							<div
								className="ag-theme-alpine"
								style={{
									height: "100%",
									width: "100%",
								}}
							>
								<AgGridReact
									rowData={tableData}
									columnDefs={tableColumns}
									defaultColDef={defaultColDef}
									undoRedoCellEditing={true}
									undoRedoCellEditingLimit={20}
									suppressCopyRowsToClipboard={true}
									defaultExcelExportParams={
										defaultExcelExportSettings
									}
									rowSelection="multiple"
									domLayout="autoHeight"
									ref={grid}
									enableRangeSelection={true}
									cacheQuickFilter={true}
									onCellClicked={onCellClicked}
									onFirstDataRendered={onFirstDataRendered}
								/>
							</div>
						</>
					)}
				</div>
			</div>
			<Modal
				open={open}
				title={strings?.modal?.title}
				onCancel={closeModalAndCleanState}
				width={1200}
				footer={(_, { OkBtn, CancelBtn }) => (
					<div className={classes.createAccountDiv}>
						<Button
							type="primary"
							size="large"
							// className="bg-blue-600"
							style={{ backgroundColor: "#4F16C2" }}
							onClick={InsertDriver}
						>
							{strings?.modal.createAccountButton}
						</Button>
					</div>
				)}
			>
				<Divider />
				<div className="">
					<div className={classes.modalInputsContainer}>
						<DefaultInput
							className={classes.inputModal}
							label={strings?.modalInputs.fullName.label}
							placeholder={
								strings?.modalInputs.fullName.placeholder
							}
							value={fullName}
							onChange={(e) => setFullName(e.target.value)}
						/>
						<DefaultInput
							className={classes.inputModal}
							label={strings?.modalInputs.email.label}
							placeholder={strings?.modalInputs.email.placeholder}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
						<DefaultPhoneInput
							className={classes.inputPhoneModal}
							label={strings?.modalInputs.phone.label}
							placeholder={strings?.modalInputs.phone.placeholder}
							initialPhoneNumber={phone}
							onPhoneNumberChange={onDriverPhoneNumberChange}
						/>
						<DefaultInput
							className={classes.inputModal}
							label={
								strings?.modalInputs.identificationNumber.label
							}
							placeholder={
								strings?.modalInputs.identificationNumber
									.placeholder
							}
							value={identificationNumber}
							onChange={(e) =>
								setIdentificationNumber(e.target.value)
							}
						/>
						<DefaultSelect
							className={classes.inputModal}
							label={strings?.modalInputs.countrySelect.label}
							flexDirection="col"
							alignCenter="start"
							placeholder={
								strings?.modalInputs.countrySelect.placeholder
							}
							options={countryDropDown}
							value={country}
							onChange={onCountrySelect}
						/>
						<DefaultSelect
							className={classes.inputModal}
							label={strings?.modalInputs.citySelect.label}
							flexDirection="col"
							alignCenter="start"
							placeholder={
								strings?.modalInputs.citySelect.placeholder
							}
							options={cityDropDown}
							value={city}
							onChange={onCitySelect}
						/>
						<DefaultSelect
							className={classes.inputModal}
							label={strings?.modalInputs.sectionSelect.label}
							flexDirection="col"
							alignCenter="start"
							placeholder={
								strings?.modalInputs.sectionSelect.placeholder
							}
							options={sectionDropDown}
							value={sectionId}
							onChange={setSectionId}
						/>
						<DefaultInput
							className={classes.inputModal}
							label={strings?.modalInputs.address.label}
							placeholder={
								strings?.modalInputs.address.placeholder
							}
							value={address}
							onChange={(e) => setAddress(e.target.value)}
						/>
					</div>
					<Divider />
				</div>
				<div className={classes.userInfos}>
					<DefaultInput
						className={classes.inputModal}
						label={strings?.modalInputs.username.label}
						value={username}
						onChange={(e) => setUsername(e.target.value)}
					/>
					<DefaultPasswordInput
						className={classes.inputModal}
						label={strings?.modalInputs.password.label}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						type="password"
						onBlur={handleBlur}
						allowClear={true}
					/>
					<DefaultInput
						label={strings?.modalInputs.confirmPassword.label}
						className={classes.inputModal}
						value={confirmPassword}
						onChange={handleConfirmPasswordChange}
						onBlur={handleBlur}
						placeholder={
							strings?.modalInputs.confirmPassword.placeholder
						}
						status={error ? "error" : ""}
						type="password"
						suffix={
							error ? (
								<CloseCircleTwoTone twoToneColor="#ff4d4f" />
							) : (
								<CheckCircleTwoTone twoToneColor="#52c41a" />
							)
						}
						allowClear={true}
					/>
				</div>

				<div className={classes.infosContainer}>
					<div className={classes.infos}>
						<p className="font-Montserrat-Medium text-14 mt-1">
							{strings?.infos.loginInfo}
						</p>
						<p className="font-Montserrat-Medium text-14 mt-1">
							{strings?.infos.mandatoryFieldsInfo}
						</p>
						<p className="font-Montserrat-Medium text-14 mt-1">
							{strings?.infos.formAccuracyInfo}
						</p>
					</div>
					<Divider />
				</div>
			</Modal>
		</>
	);
};

export default Driver;
